<template>
  <div :class="$style.chatWindow">
    <!-- 알림 컴포넌트 추가 -->
    <Notification
      v-if="notification"
      :class="$style.localNotification"
      :message="notification.message"
      :type="notification.type"
      @close="closeNotification"
    />

    <div :class="$style.chatHeader">
      <button :class="$style.backButton" aria-label="뒤로가기" @click="$emit('close')">
        <img :class="$style.backIcon" :src="backIconUrl" alt="뒤로가기" />
      </button>
      <div :class="$style.titleGroup">
        <h5>{{ ticket.id }}</h5>
        <span :class="$style.subtitle">{{ formatRelativeTime(ticket.createdAt as Timestamp) }}</span>
      </div>
    </div>
    <!-- 자동 종료 임박 배너 (종료되지 않았고, 임박했을 때만 표시) -->
    <div v-if="isAutoCloseImminent && !isEffectivelyClosed" :class="$style.autoCloseBanner">
      <p>⚠️ 약 {{ remainingMinutes }}분 후 자동으로 상담이 종료됩니다.</p>
    </div>
    <div ref="chatContentRef" :class="$style.chatContent">
      <!-- 로딩 또는 에러 상태 표시 -->
      <div v-if="messageLoading && ticketMessages.length === 0" :class="$style.loadingIndicator">
        <p>메시지 로딩 중...</p>
      </div>
      <div v-else-if="messageError" :class="$style.errorIndicator">
        <p>메시지를 불러오는데 실패했습니다: {{ messageError }}</p>
      </div>

      <!-- 날짜 구분선, 메시지, 상담사 배정 구분선 렌더링 -->
      <template v-for="(item, index) in processedMessagesWithSeparators" v-else :key="item.id || `item-${index}`">
        <!-- 날짜 구분선 -->
        <div v-if="item.type === 'dateSeparator'" :class="$style.dateSeparator">
          <span>{{ item.date }}</span>
        </div>

        <!-- 메시지 아이템 -->
        <MessageItem
          v-else-if="item.type === 'message'"
          :is-outgoing="item.isOutgoing"
          :message="item.messageData"
          :others-count="0"
          :show-avatar-and-name="item.showAvatar"
          :show-time="item.showTimestamp"
          @retry="handleRetryMessage"
        />

        <!-- 상담사 배정 구분선 -->
        <!-- <div v-else-if="item.type === 'separator' && item.subtype === 'agent_assigned'" :class="$style.agentSeparator">
          <span>상담사에게 배정되었습니다.</span>
        </div> -->
      </template>

      <!-- 티켓이 실질적으로 종료된 경우 (상태가 closed/resolved 이거나 자동 종료 시간 도달) -->
      <div v-if="isEffectivelyClosed && props.ticket.status !== 'resolved'" :class="$style.agentSeparator">
        <!-- resolvedAt이 있으면 수동 종료 -->
        <span v-if="props.ticket.resolvedAt">종료된 상담입니다</span>
        <!-- resolvedAt이 없으면 자동 종료로 간주 -->
        <span v-else>자동으로 종료된 상담입니다</span>
      </div>
      <!-- 'resolved' 상태는 다른 메시지를 표시할 수 있음 -->
      <div v-else-if="props.ticket.status === 'resolved'" :class="$style.agentSeparator">
        <span>해결된 상담입니다</span>
      </div>

      <!-- 종료 상태 입력 영역 (실질적으로 종료되었을 때 표시) -->
      <div v-if="isEffectivelyClosed" :class="$style.closedInputArea">
        <div :class="$style.feedbackContainer">
          <button
            :class="[$style.feedbackButton, $style.primary]"
            :disabled="!!ticket.customerFeedbackId"
            @click="handleFeedbackSubmit"
          >
            {{ ticket.customerFeedbackId ? "피드백 작성이 완료되었습니다." : "이번 상담은 어떠셨나요?" }}
          </button>
        </div>
        <div :class="$style.newChatContainer">
          <button :class="[$style.feedbackButton, $style.secondary]" @click="startNewChat">새로운 문의 시작하기</button>
        </div>
      </div>

      <!-- 챗봇 종료 메시지에서 안내했다면 중복 표시 방지 -->
      <!-- <div v-else-if="props.ticket.status === 'waiting_for_agent'" :class="$style.waitingForAgent">
        <p>잠시만 기다려주시면 상담사를 연결해 드릴게요.</p>
        <span>보통 1분 이내에 연결됩니다.</span>
      </div> -->
    </div>
    <div v-if="!isEffectivelyClosed" :class="$style.chatInputArea">
      <!-- 파일 미리보기 영역 -->
      <div :class="$style.filePreviewWrapper">
        <div v-if="selectedFiles.length > 0" :class="$style.filePreviewContainer">
          <div v-for="(file, index) in selectedFiles" :key="index" :class="$style.filePreviewItem">
            <img
              v-if="file.type.startsWith('image/')"
              :alt="file.name"
              :class="$style.fileThumbnail"
              :src="file.previewUrl ?? undefined"
            />
            <div v-else :class="$style.fileIconPlaceholder">{{ getFileExtension(file.name) }}</div>
            <button :class="$style.removeFileButton" aria-label="파일 제거" @click="removeFile(index)">
              <img :class="$style.removeIcon" :src="closeIconUrl" alt="제거" />
            </button>
          </div>
        </div>
      </div>

      <!-- 선택지 버튼 영역 -->
      <div
        v-if="availableOptions.length > 0 && props.ticket.status === 'chatbot_handling'"
        :class="$style.choicesInputContainer"
      >
        <button
          v-for="(choice, index) in availableOptions"
          :key="index"
          :class="$style.inputChoiceButton"
          @click="handleOptionClick(choice)"
        >
          {{ choice }}
        </button>
      </div>
      <div
        v-else-if="
          !(showFileInput || showInput) && availableOptions.length === 0 && props.ticket.status === 'chatbot_handling'
        "
        style="padding: 0 16px"
      >
        <button :class="$style.fileUploadButton" @click="endChatbotHandling">직접 문의하기</button>
      </div>

      <!-- 파일 업로드 버튼 영역 (preview.vue 스타일 참고) -->
      <div v-if="showFileInput" :class="$style.fileInputContainer">
        <!-- 숨겨진 실제 파일 입력 -->
        <input
          ref="fileInputRef"
          accept="image/*, video/*, application/pdf, .zip, .txt"
          hidden
          multiple
          type="file"
          @change="handleFileSelectAsEvent"
        />
        <!-- 사용자에게 보이는 버튼 -->
        <button :class="$style.fileUploadButton" @click="triggerFileInput">파일 업로드</button>
      </div>

      <!-- 텍스트 입력 영역 -->
      <div v-if="showInput" :class="$style.textInputContainer">
        <button :class="$style.attachButton" aria-label="파일 첨부" @click="triggerFileInput">
          <img :class="$style.attachIcon" :src="attachIconUrl" alt="파일 첨부" />
        </button>
        <input
          ref="fileInputRef"
          accept="image/*, video/*, application/pdf, .zip, .txt"
          hidden
          multiple
          type="file"
          @change="handleFileSelect"
        />
        <textarea
          ref="textareaRef"
          v-model="newMessageText"
          :class="$style.textInputField"
          :disabled="sendingMessage"
          placeholder="문의 내용을 입력해 주세요"
          rows="1"
          @input="adjustTextareaHeight"
          @keydown.enter.exact.prevent="handleEnterKey"
          @keydown.shift.enter.exact.prevent="handleShiftEnter"
        />
        <!-- Inline SVG Spinner -->
        <svg
          v-if="sendingMessage"
          :class="$style.sendingSpinner"
          fill="none"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="opacity-75"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            fill="currentColor"
          ></path>
        </svg>
      </div>
    </div>

    <!-- 피드백 다이얼로그 -->
    <FeedbackDialog
      v-if="showFeedbackDialog"
      :ticket="props.ticket"
      @close="handleFeedbackClose"
      @show-notification="showNotification"
    />

    <!-- 고객 정보 입력 다이얼로그 -->
    <CustomerInfoDialog
      :is-visible="showCustomerInfoModal"
      :ticket="props.ticket"
      @close-dialog="handleCustomerInfoClose"
      @submit-success="handleCustomerInfoSubmitSuccess"
      @submit-failure="handleCustomerInfoSubmitFailure"
    />

    <SelectProductModal
      :is-visible="showSelectProductModal"
      @close="handleSelectProductModalClose"
      @select="handleSelectProductModalSelect"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, useCssModule, watch } from "vue"
import Notification from "@/components/Notification.vue"
import { useTicket } from "@/composables/useTicket"
import closeIconUrl from "@/assets/icons/close-icon.svg"
import backIconUrl from "@/assets/icons/back-arrow-icon.svg"
import attachIconUrl from "@/assets/icons/attach-icon.svg"
import { getDateFromTimestamp } from "@/utils/chat"
import MessageItem from "./MessageItem.vue"
import FeedbackDialog from "./FeedbackDialog.vue"
import CustomerInfoDialog from "./CustomerInfoDialog.vue"
import SelectProductModal from "./SelectProductModal.vue"
import type {
  CustomerSnapshot,
  Order,
  OrderDetail,
  OrderSnapshot,
  Ticket,
  TicketMessageUnion,
  TicketStatus,
} from "@wowpress/types"
import { serverTimestamp, type Timestamp } from "firebase/firestore"
import { createWowpressCustomerMachine } from "@/xstate"
import {
  __unsafe_getAllOwnEventDescriptors,
  Actor,
  type AnyStateMachine,
  createActor,
  type StateFrom,
  type Subscription,
} from "xstate"
import { useSetting } from "@/composables/useSetting"
import { formatRelativeTime } from "@/utils/times"
import { useWowpress } from "@/composables/useWowpress"
import { useAuth } from "@/composables/useAuth"
import { ordInfoToSnapshot } from "@/utils/mapper"
import { recordChoiceClick } from "@/services/stats"

// --- Props ---
const props = defineProps<{
  ticket: Ticket
  initialState: "preOrder" | "postOrder" | "serviceInquiry"
}>()

// --- Emits ---
const emit = defineEmits(["close", "start-new-chat"])

// 로컬 알림 상태 추가
const notification = ref<{ type: "info" | "error"; message: string } | null>(null)

// 로컬 알림 표시 함수 추가
const showNotification = (payload: { type: "info" | "error"; message: string }) => {
  console.log(`[ChatWindow] Notification: ${payload.type} - ${payload.message}`)
  notification.value = payload
  // 5초 후 알림 자동 해제 (ChatExpanded와 유사하게)
  setTimeout(() => {
    if (notification.value && notification.value.message === payload.message) {
      notification.value = null
    }
  }, 5000)
}

// 로컬 알림 닫기 함수 추가
const closeNotification = () => {
  notification.value = null
}

const readonlyStatus: TicketStatus[] = ["closed", "resolved"]

// --- useTicket ---
const { ticketMessages, messageLoading, messageError, addMessage, ticketLoading, updateTicket, addBotMessage } =
  useTicket()

const {
  fetchSettings,
  autoClose, // 자동 종료 설정 가져오기
  loading: settingLoading, // 설정 로딩 상태
  error: settingError, // 설정 에러 상태
} = useSetting()

const { user } = useAuth()

const { getOrderById, me: wowpressMe } = useWowpress()

// UI 로딩 상태
const sendingMessage = ref(false)
const currentTime = ref(new Date()) // 현재 시간을 저장할 ref
let timerId: number | null = null // 타이머 ID
const showFeedbackDialog = ref(false) // 피드백 다이얼로그 표시 상태
const showCustomerInfoModal = ref(false) // 고객 정보 입력 모달 상태 추가
const showSelectProductModal = ref(false) // 상품 선택 모달 표시 상태

// --- Refs & State ---
const $style = useCssModule()
const chatContentRef = ref<HTMLElement | null>(null)
const newMessageText = ref("")
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFiles = ref<SelectedFile[]>([])

const coreActor = ref<Actor<AnyStateMachine> | null>(null)
const snapshot = ref<StateFrom<AnyStateMachine> | null>(null)
const machineLoading = ref(true)
const machineError = ref<{ type: "load" | "definition"; path?: string; message: string } | null>(null)

let stateSubscription: Subscription | null = null

interface SelectedFile {
  file: File
  name: string
  type: string
  previewUrl: string | null
}

// --- Computed ---
type ProcessedItem =
  | { type: "dateSeparator"; date: string; id: string }
  | {
      type: "message"
      messageData: TicketMessageUnion
      isOutgoing: boolean
      showAvatar: boolean
      showTimestamp: boolean
      id: string
    }
  | { type: "separator"; subtype: "agent_assigned"; id: string }

const processedMessagesWithSeparators = computed<ProcessedItem[]>(() => {
  const result: ProcessedItem[] = []
  let lastDateString: string | null = null
  let lastAuthorId: string | null = null
  let assignedSeparatorInserted = false

  const assignedDate = props.ticket.assignedAt?.toDate()

  console.log("[ChatWindow] processedMessagesWithSeparators", props.ticket.assignedAt)

  ticketMessages.value.forEach((message, index) => {
    const currentMsgDate = getDateFromTimestamp(message.createdAt)
    const currentDateString = currentMsgDate?.toLocaleDateString()
    let currentAuthorId: string | undefined
    if (message.author) {
      currentAuthorId = message.author.id
    }
    const currentHour = currentMsgDate?.getHours()
    const currentMinute = currentMsgDate?.getMinutes()

    // 1. 날짜 구분선 추가 로직
    if (currentDateString && currentDateString !== lastDateString) {
      result.push({ type: "dateSeparator", date: currentDateString, id: `date-${currentDateString}` })
      lastDateString = currentDateString
      lastAuthorId = null
    }

    // 2. 상담사 배정 구분선 추가 로직
    if (assignedDate && currentMsgDate && !assignedSeparatorInserted) {
      if (currentMsgDate > assignedDate) {
        console.log("[ChatWindow] Agent assigned separator insertion point found before message:", message.id || index)
        result.push({
          type: "separator",
          subtype: "agent_assigned",
          id: `sep-assign-${message.id || index}`,
        })
        assignedSeparatorInserted = true
      }
    }

    // 3. 메시지 처리 로직 수정 (showTimestamp)
    const isOutgoing = message.author?.type === "customer"
    const showAvatar = currentAuthorId !== lastAuthorId || !lastAuthorId

    let showTimestamp = false
    const nextMessage = ticketMessages.value[index + 1]
    if (!nextMessage) {
      showTimestamp = true
    } else {
      const nextMsgDate = getDateFromTimestamp(nextMessage.createdAt)
      const nextAuthorId = nextMessage.author?.id
      const nextHour = nextMsgDate?.getHours()
      const nextMinute = nextMsgDate?.getMinutes()

      if (nextAuthorId !== currentAuthorId || nextHour !== currentHour || nextMinute !== currentMinute) {
        showTimestamp = true
      }
    }

    result.push({
      type: "message",
      messageData: message,
      isOutgoing: isOutgoing,
      showAvatar: showAvatar,
      showTimestamp: showTimestamp,
      id: message.id || `msg-${index}`,
    })

    // 다음 메시지 비교를 위해 현재 상태 업데이트
    lastAuthorId = currentAuthorId ?? null
  })

  return result
})

const canSendMessage = computed(() => {
  return (
    (newMessageText.value.trim().length > 0 || selectedFiles.value.length > 0) &&
    !ticketLoading.value &&
    !sendingMessage.value
  )
})

// --- 자동 종료 관련 Computed 속성 ---
const autoCloseEnabled = computed(
  () => !settingLoading.value && !settingError.value && autoClose.value?.enabled === true,
)

const autoCloseTime = computed<Date | null>(() => {
  if (!autoCloseEnabled.value || !props.ticket.lastMessage?.createdAt || !autoClose.value?.expireAfter) {
    return null
  }
  const lastMessageTime = (props.ticket.lastMessage.createdAt as Timestamp).toDate()
  // expireAfter가 분 단위이므로 60 * 1000을 곱하여 밀리초로 변환
  return new Date(lastMessageTime.getTime() + autoClose.value.expireAfter * 60 * 1000)
})

const remainingSeconds = computed<number | null>(() => {
  if (!autoCloseTime.value || readonlyStatus.includes(props.ticket.status)) {
    return null
  }
  const diff = autoCloseTime.value.getTime() - currentTime.value.getTime()
  return Math.max(0, Math.floor(diff / 1000)) // 초 단위, 음수 방지
})

const remainingMinutes = computed<number | null>(() => {
  if (remainingSeconds.value === null) return null
  return Math.ceil(remainingSeconds.value / 60) // 분 단위 (올림)
})

const isAutoCloseImminent = computed<boolean>(() => {
  // 1. 자동 종료 기능 활성화 확인
  // 2. 남은 시간 계산 가능 확인 (5분 미만)
  // 3. 현재 티켓 상태가 autoClose 설정의 targetStatus와 일치하는지 확인
  // 4. 현재 티켓이 이미 종료/해결 상태가 아닌지 확인
  return (
    autoCloseEnabled.value &&
    remainingSeconds.value !== null &&
    remainingSeconds.value < 300 && // 5분 미만
    props.ticket.status === autoClose.value?.targetStatus && // 상태 일치 확인 추가
    !readonlyStatus.includes(props.ticket.status)
  )
})

const isEffectivelyClosed = computed<boolean>(() => {
  // 실제 상태가 종료/해결 이거나, 자동 종료 시간이 지났을 때 (remainingSeconds <= 0)
  const value =
    readonlyStatus.includes(props.ticket.status) ||
    (autoCloseEnabled.value &&
      remainingSeconds.value !== null &&
      remainingSeconds.value <= 0 &&
      props.ticket.status === autoClose.value?.targetStatus)
  if (value && !props.ticket.resolvedAt) {
    console.log("[ChatWindow] Ticket is forced to be closed:", props.ticket.id)
    updateTicket(props.ticket.id, { status: "closed" })
  }

  return value
})

// --- Helper Methods ---
const getFileExtension = (filename: string): string => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toUpperCase()
}

// 현재 스크롤이 맨 아래에 있는지 확인하는 함수
const isAtBottom = (): boolean => {
  if (!chatContentRef.value) return true // 참조가 없으면 스크롤 허용
  const { scrollTop, scrollHeight, clientHeight } = chatContentRef.value
  // 맨 아래에서 100px 이내면 맨 아래로 간주 (더 관대하게)
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight
  console.debug("[ChatWindow] isAtBottom check:", { scrollTop, scrollHeight, clientHeight, distanceFromBottom })
  return distanceFromBottom < 100
}

const scrollToBottom = async (behavior: "smooth" | "auto" = "auto", force = false) => {
  // 강제 스크롤이 아니고 맨 아래에 있지 않다면 스크롤하지 않음
  if (!force && !isAtBottom()) {
    console.debug("[ChatWindow] Skipping scroll - not at bottom")
    return
  }

  // 모바일에서는 더 빠른 타이밍으로 처리
  const isMobile = window.innerWidth <= 480
  const delay = isMobile ? 50 : 200

  await new Promise((resolve) => setTimeout(resolve, delay))
  await nextTick()

  if (chatContentRef.value) {
    // 모바일에서는 더 부드럽게 처리하기 위해 auto 동작 사용
    const finalBehavior = isMobile ? "auto" : behavior
    console.debug("[ChatWindow] Scrolling to bottom with behavior:", finalBehavior)
    chatContentRef.value.scrollTo({ top: chatContentRef.value.scrollHeight, behavior: finalBehavior })
  }
}
const adjustTextareaHeight = () => {
  if (textareaRef.value) {
    textareaRef.value.style.height = "auto"
    textareaRef.value.style.height = Math.min(textareaRef.value.scrollHeight, 100) + "px" // 최대 높이 제한
  }
}
const triggerFileInput = () => {
  fileInputRef.value?.click()
}
const endChatbotHandling = async () => {
  if (!coreActor.value || !props.ticket.id) {
    console.error("State machine or ticket ID is not available.")
    return // 서비스나 티켓 ID 없으면 종료
  }

  coreActor.value.send({ type: "xstate.END_CHATBOT" })

  // 티켓 데이터에 고객 이름과 전화번호가 이미 있는지 확인
  // 스냅샷의 컨텍스트 대신 최신 티켓 프롭스를 사용하는 것이 더 안전할 수 있음
  const customerName = props.ticket.customer?.name
  const customerPhone = props.ticket.customer?.phone

  if (customerName && customerPhone) {
    // 정보가 이미 있으면 바로 상담사 대기 상태로 전환
    await markAsWaitingForAgent()
  } else {
    // 정보가 없으면 고객 정보 입력 모달 표시
    showCustomerInfoModal.value = true
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target && target.files) {
    const files = Array.from(target.files)
    files.forEach((file) => {
      if (selectedFiles.value.length >= 5) {
        // 최대 5개 첨부 제한 (예시)
        alert("파일은 최대 5개까지 첨부할 수 있습니다.")
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result
        if (result && typeof result === "string") {
          selectedFiles.value.push({
            file,
            name: file.name,
            type: file.type,
            previewUrl: file.type.startsWith("image/") ? result : null, // 이미지인 경우만 미리보기 URL 저장
          })
        }
      }
      reader.readAsDataURL(file) // 이미지 미리보기를 위해 읽기
    })
    target.value = "" // 같은 파일 다시 선택 가능하도록 초기화
  }
}

const handleFileSelectAsEvent = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files

  if (!files || files.length === 0) {
    return // 파일이 없으면 종료
  }

  // 파일 개수 제한 확인 (최대 5개)
  if (files.length > 5) {
    alert("파일은 한 번에 최대 5개까지 업로드할 수 있습니다.")
    // 입력 필드 초기화 (선택된 파일 제거)
    if (fileInputRef.value) {
      fileInputRef.value.value = ""
    }
    return // 처리 중단
  }

  if (!coreActor.value || !props.ticket.id) {
    // activeTicketId 대신 props.ticket.id 사용
    console.error("State machine or ticket ID is not available.")
    return // 서비스나 티켓 ID 없으면 종료
  }

  let allSuccess = true // 전체 성공 여부 플래그
  let firstError: Error | null = null // 첫 번째 발생한 오류 저장
  // 각 파일 업로드 시작 전 이벤트 전송
  coreActor.value.send({ type: "xstate.FILE_UPLOAD_START" })

  // 여러 파일 처리
  for (const file of Array.from(files)) {
    try {
      // useTicket의 addMessage 함수 사용
      const success = await addMessage(props.ticket.id, file, {
        inputType: "file",
      })

      if (!success) {
        allSuccess = false // 하나라도 실패하면 전체 실패
        if (!firstError) {
          // 첫 번째 에러만 저장
          firstError = messageError.value || new Error(`${file.name} 전송 실패`)
        }
        // 실패 시 루프를 중단할 수도 있지만, 모든 파일 시도를 위해 계속 진행
      }
    } catch (error) {
      allSuccess = false // 예외 발생 시 전체 실패
      console.error(`[ChatWindow] Error handling file ${file.name}:`, error)
      if (!firstError) {
        // 첫 번째 에러만 저장
        firstError = error instanceof Error ? error : new Error(`알 수 없는 오류로 ${file.name} 전송 실패`)
      }
      // 예외 발생 시 루프를 중단할 수도 있지만, 모든 파일 시도를 위해 계속 진행
    }
  }

  // 모든 파일 처리 후 최종 결과 이벤트 전송
  if (allSuccess) {
    coreActor.value.send({ type: "xstate.FILE_UPLOAD_SUCCESS" }) // output 없이 성공 이벤트 전송
  } else {
    coreActor.value.send({
      type: "xstate.FILE_UPLOAD_FAILURE",
      error: firstError || new Error("파일 전송 중 알 수 없는 오류 발생"), // 저장된 첫 번째 에러 또는 기본 에러
    })
  }

  // 모든 파일 처리 후 입력 필드 초기화
  if (fileInputRef.value) {
    fileInputRef.value.value = ""
  }
}

const removeFile = (index: number) => {
  selectedFiles.value.splice(index, 1)
}
const handleRetryMessage = async (messageId: string) => {
  console.log("Retry message requested:", messageId)

  // 실패한 메시지 찾기
  const failedMessage = ticketMessages.value.find((msg) => msg.id === messageId && msg.status === "error")
  if (!failedMessage) {
    console.error("Failed message not found or not in error state:", messageId)
    return
  }

  // 메시지 상태를 sending으로 업데이트
  ticketMessages.value = ticketMessages.value.map((msg) => (msg.id === messageId ? { ...msg, status: "sending" } : msg))

  try {
    let success = false

    // 메시지 타입에 따라 재전송 로직 실행
    if (failedMessage.type === "text") {
      // 텍스트 메시지 재전송
      success = await addMessage(props.ticket.id, failedMessage.text, {
        inputType: "text",
      })
    } else if (failedMessage.type === "image" || failedMessage.type === "file") {
      // 이미지나 파일 메시지는 원본 파일이 없으므로 사용자에게 알림
      alert("파일 메시지는 재전송이 불가능합니다. 새로운 파일을 선택해주세요.")

      // 메시지 상태를 다시 error로 변경
      ticketMessages.value = ticketMessages.value.map((msg) =>
        msg.id === messageId ? { ...msg, status: "error" } : msg,
      )
      return
    }

    if (!success) {
      // 재전송 실패 시 상태를 다시 error로 변경
      ticketMessages.value = ticketMessages.value.map((msg) =>
        msg.id === messageId ? { ...msg, status: "error" } : msg,
      )
      alert("메시지 재전송에 실패했습니다.")
    }
  } catch (error) {
    console.error("Error retrying message:", error)

    // 에러 발생 시 상태를 다시 error로 변경
    ticketMessages.value = ticketMessages.value.map((msg) => (msg.id === messageId ? { ...msg, status: "error" } : msg))
    alert("메시지 재전송 중 오류가 발생했습니다.")
  }
}

// --- 메시지/파일 전송 ---
const handleSend = async () => {
  if (!canSendMessage.value) return

  sendingMessage.value = true
  const textToSend = newMessageText.value.trim()
  const filesToSend = [...selectedFiles.value] // 복사본 사용

  newMessageText.value = ""
  selectedFiles.value = []
  adjustTextareaHeight()

  let success = true

  try {
    // 텍스트 메시지 전송
    if (textToSend) {
      success &&= await addMessage(props.ticket.id, textToSend, {
        inputType: "text",
      })
      if (!success) {
        console.error("Failed to send text message.", messageError.value)
        newMessageText.value = textToSend // 실패 시 복원
      }
    }

    // 파일 메시지 전송
    if (success && filesToSend.length > 0) {
      // 텍스트 성공 시에만 파일 전송 시도 (선택적)
      // 모든 파일을 동시에 전송하여 각각의 로딩 상태를 표시
      const fileUploadPromises = filesToSend.map((fileItem) =>
        addMessage(props.ticket.id, fileItem.file, {
          inputType: "file",
        }),
      )

      // 모든 파일 업로드 결과 처리
      const results = await Promise.all(fileUploadPromises)

      // 모든 결과가 성공인지 확인
      success = results.every((result) => result === true)

      // 실패한 파일이 있으면 로그 출력
      if (!success) {
        const failedFiles = filesToSend.filter((_, index) => !results[index])
        failedFiles.forEach((file) => {
          console.error(`Failed to send file: ${file.name}`, messageError.value)
        })
      }
    }

    if (success) {
      console.log("모든 메시지 전송 성공")
    } else if (messageError.value) {
      // 일부 실패 시
      console.error("메시지 전송 실패:", messageError.value)
      alert("메시지 전송에 실패했습니다.")
    } else {
      alert("메시지 전송에 일부 실패했습니다.")
    }
  } catch (error) {
    console.error("Error during message sending:", error)
    alert("메시지 전송 중 오류가 발생했습니다.")
    success = false // 에러 발생 시 실패 처리
    // 실패 시 입력 내용 복원 등 추가 처리 필요 시 여기에 구현
  } finally {
    sendingMessage.value = false
    // DOM 업데이트 완료 후 포커스 설정
    await nextTick()
    textareaRef.value?.focus()
  }
}

// Shift + Enter 처리
const handleShiftEnter = () => {
  newMessageText.value += "\n"
  adjustTextareaHeight()
  // 줄바꿈 후 포커스 설정 (필요 시)
  textareaRef.value?.focus()
}

const showInput = computed(() => {
  // isChatInputEnabled 컨텍스트는 그대로 사용
  return (
    (typeof snapshot.value?.context?.isChatInputEnabled === "boolean"
      ? snapshot.value.context.isChatInputEnabled
      : false) || props.ticket.status !== "chatbot_handling"
  )
})

const showFileInput = computed(() => {
  // showFileInput 컨텍스트는 그대로 사용
  return (
    (typeof snapshot.value?.context?.showFileInput === "boolean" ? snapshot.value.context.showFileInput : false) &&
    props.ticket.status === "chatbot_handling"
  )
})

const availableOptions = computed(() => {
  if (!snapshot.value) return []

  try {
    // 1. Get potential event names
    const events = (snapshot.value as any).nextEvents || __unsafe_getAllOwnEventDescriptors(snapshot.value as any)
    let eventNames: string[] = Array.isArray(events) ? events : Object.keys(events)

    // 2. Basic Filtering (ChatWindow specific)
    const validEventTypes = eventNames.filter(
      (ev) =>
        !ev.startsWith("xstate.") &&
        ev !== "done.state.wowpressBot.end_chat" &&
        ev !== "USER_MESSAGE" && // Ensure USER_MESSAGE is filtered out if needed
        canSendEvent(ev),
    )

    // 3. Get Order Information using Snapshot Meta
    let orderedEvents: string[] = []
    const getBaseKey = (key: string) => key.replace(/^xstate\./, "")

    try {
      // Use snapshot.value.getMeta() to get meta information
      const metaOrder = Object.values(snapshot.value?.getMeta() || {})?.[0]?.eventOrder as string[] | undefined

      if (metaOrder) {
        // Sort based on metaOrder found in snapshot
        console.debug("[ChatWindow] Sorting options based on metaOrder:", metaOrder)
        const orderMap = new Map(metaOrder.map((key, index) => [key, index]))
        orderedEvents = [...validEventTypes].sort((a, b) => {
          const orderA = orderMap.get(a)
          const orderB = orderMap.get(b)
          if (orderA !== undefined && orderB !== undefined) return orderA - orderB // Both in metaOrder
          if (orderA !== undefined) return -1 // a is in order, b is not
          if (orderB !== undefined) return 1 // b is in order, a is not
          return getBaseKey(a).localeCompare(getBaseKey(b)) // Neither in order, sort alphabetically by base key
        })
      } else {
        // Sort alphabetically by base key if no metaOrder found
        console.debug("[ChatWindow] No metaOrder found in snapshot, sorting alphabetically by base key.")
        orderedEvents = [...validEventTypes].sort((a, b) => getBaseKey(a).localeCompare(getBaseKey(b)))
      }
    } catch (metaError) {
      console.error("[ChatWindow] Error accessing meta from snapshot:", metaError)
      // Fallback to simple alphabetical sort if accessing meta fails
      orderedEvents = [...validEventTypes].sort((a, b) => getBaseKey(a).localeCompare(getBaseKey(b)))
    }

    return orderedEvents
  } catch (e) {
    console.error("Error processing available options in ChatWindow:", e)
    // Keep existing fallback logic, ensuring canSendEvent check
    const potentialEvents = ["BACK", "END_CHAT"]
    return potentialEvents.filter((ev) => snapshot.value?.can({ type: ev } as any) && canSendEvent(ev)).reverse()
  }
})

const canSendEvent = (eventType: string): boolean => {
  if (!snapshot.value) return false
  const eventObject = { type: eventType }
  try {
    return snapshot.value.can(eventObject)
  } catch (e) {
    console.warn(`Error checking if event '${eventType}' can be sent:`, e)
    return false
  }
}

const markAsClosed = async () => {
  await updateTicket(props.ticket.id, { status: "closed", resolvedAt: serverTimestamp() as Timestamp })
}

const markAsWaitingForAgent = async () => {
  await updateTicket(props.ticket.id, { status: "waiting_for_agent" })
}

const handleOptionClick = async (eventType: string) => {
  if (!canSendEvent(eventType)) {
    console.warn(`Attempted to send event '${eventType}' which is not currently allowed.`)
    return
  }
  console.log("[ChatWindow] handleOptionClick sending event:", eventType)
  // 사용자 선택 메시지 추가 (선택 사항, UI/UX 고려)
  await addMessage(props.ticket.id, eventType, {
    inputType: "choice",
  })
  // 선택지 클릭 통계 기록
  await recordChoiceClick({
    choiceText: eventType,
    timestamp: Date.now(),
    ticketId: props.ticket.id,
    userId: user.value?.uid,
  })

  coreActor.value?.send({ type: eventType })

  // event type으로 state 가져오기
  const state = coreActor.value?.getSnapshot()
  if (state?.value === "end_chat") {
    await markAsClosed()
  } else if (state?.value === "additional_inquiry") {
    // 티켓 데이터에 고객 이름과 전화번호가 이미 있는지 확인
    // 스냅샷의 컨텍스트 대신 최신 티켓 프롭스를 사용하는 것이 더 안전할 수 있음
    const customerName = props.ticket.customer?.name
    const customerPhone = props.ticket.customer?.phone

    if (customerName && customerPhone) {
      // 정보가 이미 있으면 바로 상담사 대기 상태로 전환
      await markAsWaitingForAgent()
    } else {
      // 정보가 없으면 고객 정보 입력 모달 표시
      showCustomerInfoModal.value = true
    }
  }
}

// 고객 정보 제출 성공 시 처리
const handleCustomerInfoSubmitSuccess = async (name: string) => {
  console.log("[ChatWindow] Customer info submitted successfully. Marking as waiting for agent.")
  showCustomerInfoModal.value = false
  addBotMessage(props.ticket.id, `안녕하세요 ${name}님!`)
  addBotMessage(
    props.ticket.id,
    "이제 직접 문의하실 수 있어요. 업무시간 이외에는 답변이 늦을수 있어서 양해 부탁드려요.",
  )
  await markAsWaitingForAgent()
}

// 고객 정보 모달 닫기 처리 (단순 닫기)
const handleCustomerInfoClose = () => {
  console.log("[ChatWindow] Customer info dialog closed by user.")
  showCustomerInfoModal.value = false
  addBotMessage(props.ticket.id, "고객 정보를 입력을 완료하셔야 문의를 진행할 수 있어요.")
}

// 고객 정보 제출 실패 시 처리
const handleCustomerInfoSubmitFailure = (error: Error) => {
  console.error("[ChatWindow] Customer info submission failed:", error)
  showCustomerInfoModal.value = false
  addBotMessage(props.ticket.id, "정보 업데이트 실패: " + error.message)
}

watch(
  () => props.ticket,
  (newTicket) => {
    console.log("[ChatWindow] Ticket status changed:", newTicket.status)
    // 티켓 상태가 변경되면 자동 종료 관련 상태 재계산 필요
    // (computed 속성은 자동으로 처리)
  },
  { deep: true, immediate: true },
)

// --- Machine Setup ---

const setupMachine = async () => {
  try {
    const accessToken = await user.value?.getIdToken()
    let currentOrder: Order | null = null
    const updated: { order?: OrderSnapshot; customer?: CustomerSnapshot } = {}

    if (accessToken) {
      if (props.ticket.order?.orderNo) {
        currentOrder = await getOrderById(accessToken, props.ticket.order?.orderNo)
        if (currentOrder) {
          const orderSnapshot = ordInfoToSnapshot(currentOrder)
          updated.order = orderSnapshot

          console.log("[ChatWindow] Order fetched:", currentOrder)
        }
      }

      if (props.ticket.customer?.id) {
        const wowpressCustomer = await wowpressMe(accessToken)
        if (wowpressCustomer) {
          updated.customer = wowpressCustomer

          console.log("[ChatWindow] Customer fetched:", wowpressCustomer)
        }
      } else {
        updated.customer = {
          id: user?.value?.uid,
          name: user?.value?.displayName || "",
          email: user?.value?.email || "",
        }
      }

      if (Object.keys(updated).length > 0) {
        updateTicket(props.ticket.id, updated)
      }
    } else {
      console.log("[ChatWindow] No access token. Skipping order and customer fetch.")
    }

    if (props.ticket.status !== "chatbot_handling" && props.ticket.status !== "open") {
      console.log("[ChatWindow] Ticket is not being handled by chatbot. Skipping machine setup.")
      return
    }

    machineLoading.value = true
    machineError.value = null

    if (stateSubscription) stateSubscription.unsubscribe()
    if (coreActor.value) coreActor.value.stop()
    stateSubscription = null
    coreActor.value = null
    console.log("[ChatWindow] Creating new machine instance...", props.ticket.lastState)

    let initialState = "greeting"
    if (props.initialState) {
      if (props.initialState === "postOrder") {
        initialState = "order_inquiry"
      } else if (props.initialState === "preOrder") {
        initialState = "pre_order_intro"
      }
    }

    const machineInstance = await createWowpressCustomerMachine(
      {
        ticket: {
          ...props.ticket,
          ...updated,
        },
        orderInfo: currentOrder as OrderDetail | undefined,
      },
      props.ticket.lastState || initialState,
    )

    if (!machineInstance) {
      throw new Error(`상태 머신 생성 실패. 정의 오류 가능성 있음.`)
    }

    console.log("[ChatWindow] Machine instance created successfully.")
    coreActor.value = createActor(machineInstance, {
      input: {
        ticket: props.ticket,
      },
    })

    stateSubscription = coreActor.value.subscribe((state) => {
      console.log("[ChatWindow] State updated:", state)
      snapshot.value = state
      updateTicket(props.ticket.id, { lastState: state.value.toString() })
    })

    coreActor.value.start()
    snapshot.value = coreActor.value.getSnapshot()

    console.log("[ChatWindow] Machine started successfully.", snapshot.value)
  } catch (error: any) {
    console.error("[ChatWindow] Error creating or starting XState service:", error)
    const pathMatch = error.message?.match(/Invalid transition definition for state node 'wowpressBot\.([\w\.]+)'/)
    let faultyPath: string | undefined = undefined

    if (pathMatch && pathMatch[1]) {
      faultyPath = pathMatch[1]
      console.warn(`[ChatWindow] Detected definition error in state from caught error: ${faultyPath}.`)
      machineError.value = {
        type: "definition",
        path: faultyPath,
        message: `정의 오류 감지됨: 상태 '${faultyPath}' 확인 필요. ${error.message}`,
      }
    } else {
      machineError.value = { type: "load", message: error.message || "챗봇 로딩 또는 재시작 중 오류 발생" }
    }

    // Clean up potentially partially started service
    if (stateSubscription) stateSubscription.unsubscribe()
    if (coreActor.value) coreActor.value.stop()
    coreActor.value = null
    snapshot.value = null
  } finally {
    machineLoading.value = false
  }
}

const disposeMachine = () => {
  if (stateSubscription) stateSubscription.unsubscribe()
  if (coreActor.value) coreActor.value.stop()
  coreActor.value = null
  snapshot.value = null
}

// --- Lifecycle Hooks ---
onMounted(() => {
  // 초기 로드 및 마운트 시 맨 아래로 스크롤 (강제)
  scrollToBottom("auto", true)
  if (textareaRef.value) {
    adjustTextareaHeight() // 초기 높이 조절
  }

  // 설정 로딩
  fetchSettings()

  // 머신 생성
  setupMachine()

  // 현재 시간 업데이트 타이머 시작 (1초마다)
  timerId = window.setInterval(() => {
    currentTime.value = new Date()
  }, 1000)
})

onUnmounted(() => {
  disposeMachine()
  // 타이머 정리
  if (timerId) {
    clearInterval(timerId)
    timerId = null
  }
})

// --- Watchers ---
// 메시지 목록 변경 시 스크롤 조정
watch(
  ticketMessages,
  async (newMessages, oldMessages) => {
    // 새 메시지가 추가되었을 때만 처리
    const addedNewMessage = newMessages.length > (oldMessages?.length || 0)

    if (addedNewMessage) {
      console.debug("[ChatWindow] New message added, checking scroll...")

      // 메시지 추가 전에 현재 스크롤 위치를 저장
      let wasAtBottom = true
      if (chatContentRef.value) {
        const { scrollTop, scrollHeight, clientHeight } = chatContentRef.value
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight
        // 사용자가 200px 이상 위로 스크롤했으면 의도적으로 위를 보고 있는 것으로 간주
        wasAtBottom = distanceFromBottom < 200
        console.debug("[ChatWindow] Pre-update scroll state:", { wasAtBottom, distanceFromBottom })
      }

      // DOM 업데이트 기다림
      await nextTick()

      // 메시지 추가 전에 맨 아래에 있었다면 새 메시지를 보여주기 위해 스크롤
      if (wasAtBottom) {
        console.debug("[ChatWindow] Auto-scrolling to new message")
        // 모바일에서는 더 부드러운 경험을 위해 auto 사용
        const isMobile = window.innerWidth <= 480
        scrollToBottom(isMobile ? "auto" : "smooth", true) // 강제 스크롤
      } else {
        console.debug("[ChatWindow] User was scrolled up, not auto-scrolling")
      }
    }
    // 초기 로드나 다른 변경사항에 대해서는 별도 처리하지 않음
    // onMounted에서 초기 스크롤을 처리함
  },
  { deep: true },
)

// --- 종료 상태 관련 메서드 ---
const handleFeedbackSubmit = () => {
  console.log("Feedback button clicked")
  showFeedbackDialog.value = true // 다이얼로그 표시
}

const handleFeedbackClose = () => {
  showFeedbackDialog.value = false
}

const startNewChat = () => {
  console.log("Start new chat requested from closed window")
  emit("start-new-chat")
}

// --- Enter Key Handler ---
const handleEnterKey = (event: KeyboardEvent) => {
  if (!event.isComposing) {
    handleSend()
  }
}

// 상품 선택 모달 닫기 핸들러
const handleSelectProductModalClose = () => {
  showSelectProductModal.value = false
  // 상태 머신에 선택 취소 또는 실패 이벤트 전송
  if (coreActor.value) {
    coreActor.value.send({ type: "xstate.PRODUCT_SELECTION_FAILURE" })
    console.log("[ChatWindow] Product selection modal closed, sent PRODUCT_SELECTION_FAILURE")
  }
}

// 상품 선택 완료 핸들러
const handleSelectProductModalSelect = async (product: { name: string; productNo?: string }) => {
  showSelectProductModal.value = false
  // 상태 머신에 선택 성공 이벤트와 선택된 상품 정보 전송
  if (coreActor.value) {
    await addMessage(props.ticket.id, product.name, {
      inputType: "choice",
      productName: product.name,
    })
    await recordChoiceClick({
      choiceText: product.name,
      timestamp: Date.now(),
      ticketId: props.ticket.id,
      userId: user.value?.uid,
    })
    coreActor.value.send({ type: "xstate.PRODUCT_SELECTION_SUCCESS", selectedProduct: product })
    console.log("[ChatWindow] Product selected, sent PRODUCT_SELECTION_SUCCESS with product:", product)
  }
}

watch(
  () => snapshot.value?.context?.showSelectProductModal,
  (showModal) => {
    if (typeof showModal === "boolean") {
      showSelectProductModal.value = showModal
      if (showModal) {
        console.log("[ChatWindow] showSelectProductModal context changed to true, opening modal.")
      } else {
        console.log("[ChatWindow] showSelectProductModal context changed to false, ensuring modal is closed.")
      }
    }
  },
)
</script>

<style module>
.chatWindow {
  /* Figma I90:3473;85:314 Container */
  width: 343px;
  /* Figma width */
  height: 583px;
  /* Figma height 추정 (Frame 31324 기준, 내용 길이에 따라 달라질 수 있음) */
  background-color: #ffffff;
  /* Figma: 32px 32px 16px 16px - 모바일 전체 화면 고려하여 일반적인 16px 적용 또는 상단만 적용 */
  border-radius: 16px;
  box-shadow:
    5px 14px 80px 0px rgba(26, 26, 26, 0.06),
    10px 10px 40px 0px rgba(0, 0, 0, 0.04);
  /* 패딩 대신 flex direction column으로 처리 */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: "Noto Sans KR", sans-serif;
}

.chatHeader {
  /* Figma I90:3473;85:315 Rectangle 1 배경색 일부 적용 */
  background-color: #ffecf6;
  /* 연한 분홍 배경 */
  padding: 16px 16px;
  /* Figma Frame 31326 패딩 참고 */
  display: flex;
  justify-content: flex-start;
  /* Align items to start */
  align-items: center;
  flex-shrink: 0;
  /* 헤더 높이 고정 */
  gap: 16px;
  /* Gap between back button and title */
}

.titleGroup {
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Gap between title and subtitle */
}

.titleGroup h5 {
  /* Figma I90:3473;86:186 AI Inquiry Text 스타일 참고 */
  font-weight: 700;
  font-size: 13px;
  color: #111111;
  /* 검은색 */
  margin: 0;
}

.subtitle {
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #888888;
}

.backButton {
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease-out;
  background-color: transparent;
  border: none;
}

.backButton:hover {
  transform: scale(1.05);
}

.backIcon {
  width: 16px;
  /* Assuming same icon size */
  height: 16px;
}

.chatContent {
  flex-grow: 1;
  padding: 20px 16px;
  overflow-y: auto;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* 메시지 행 간 간격 조정 */
  /* 모바일 터치 스크롤 최적화 */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.messageRow {
  display: flex;
  /* isOutgoing prop이 없으므로 클래스 바인딩 등으로 처리 필요시 추가 */
  /* 예: :class="[message.isOutgoing ? $style.outgoing : $style.incoming]" */
}

.avatarContainer {
  width: 32px;
  /* 아바타 크기 + 여백 고려 */
  margin-right: 8px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  /* 아바타를 위쪽으로 정렬 */
  justify-content: flex-start;
}

.chatAvatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.avatarPlaceholder {
  width: 32px;
  height: 32px;
}

.messageContentWrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  /* 시간 표시를 위해 */
}

.botMessage {
  background-color: #f3f4f6;
  color: #1f2937;
  padding: 12px 16px;
  border-radius: 0 8px 8px 8px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  max-width: 85%;
  align-self: flex-start;
  /* 왼쪽 정렬 유지 */
  word-break: keep-all;
  margin-bottom: 4px;
  /* 같은 그룹 내 메시지 간 간격 */
}

.userMessage {
  background-color: #ffecf6;
  /* Figma 기준 사용자 메시지 배경 */
  color: #111111;
  /* Figma 기준 사용자 메시지 텍스트 색상 */
  padding: 12px 16px;
  /* Bot 메시지와 유사하게 조정 */
  border-radius: 8px 8px 0px 8px;
  /* 우측 상단 각진 형태 */
  font-size: 13px;
  /* Bot 메시지와 통일 */
  font-weight: 400;
  /* Bot 메시지와 통일 */
  line-height: 1.4;
  max-width: 85%;
  align-self: flex-end;
  word-break: keep-all;
  margin-bottom: 4px;
  /* 같은 그룹 내 메시지 간 간격 */
}

.messageTime {
  font-size: 11px;
  /* 작은 크기 */
  color: #9ca3af;
  /* Figma Gray 400 */
  margin-top: 2px;
}

.timeIncoming {
  align-self: flex-start;
  /* 왼쪽 정렬 */
  margin-left: 4px;
}

.timeOutgoing {
  align-self: flex-end;
  /* 오른쪽 정렬 */
}

.dateSeparator,
.agentSeparator {
  /* 새로운 구분선 클래스 추가 */
  text-align: center;
  margin: 16px 0;
  color: #888;
  /* 구분선 기본 텍스트 색상 */
  font-size: 0.85em;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dateSeparator span,
.agentSeparator span {
  /* 새로운 구분선 클래스 추가 */
  background-color: var(--chat-bg-color, #ffffff);
  /* 채팅 배경색 변수 사용 */
  padding: 0 10px;
  z-index: 1;
}

/* 구분선 좌우 라인 (선택 사항) */
.dateSeparator::before,
.dateSeparator::after,
.agentSeparator::before,
  /* 새로운 구분선 클래스 추가 */
.agentSeparator::after {
  /* 새로운 구분선 클래스 추가 */
  content: "";
  flex-grow: 1;
  height: 1px;
  background-color: #e0e0e0;
  /* 라인 색상 */
  margin: 0 10px;
}

.dateSeparator::before,
.agentSeparator::before {
  margin-right: 10px;
}

/* 새로운 구분선 클래스 추가 */
.dateSeparator::after,
.agentSeparator::after {
  margin-left: 10px;
}

/* 새로운 구분선 클래스 추가 */

.chatInputArea {
  border-top: 1px solid #eeeeee;
  min-height: 72px;
  /* 기본 최소 높이 */
  max-height: 200px;
  /* 입력 영역 최대 높이 (텍스트+파일 미리보기) */
  flex-shrink: 0;
  background-color: #ffffff;
  display: flex;
  /* 내부 요소 정렬 위해 */
  flex-direction: column;
  /* 선택지/파일 미리보기 + 입력 필드 수직 배치 */
  justify-content: center;
  transition: height 0.2s ease-in-out;
  /* 높이 변경 애니메이션 */
  position: relative;
}

/* 파일 미리보기 컨테이너 */
.filePreviewWrapper {
  position: relative;
  width: 100%;
  max-width: calc(100% - 16px);
  overflow: hidden;
  z-index: 1000;
  top: -80px;
  position: absolute;
}

.filePreviewContainer {
  width: auto;
  display: flex;
  overflow-x: auto;
  /* 가로 스크롤 활성화 */
  padding: 8px 8px;
  gap: 8px;
  /* 미리보기 아이템 간 간격 */
  scrollbar-width: none;
  /* Firefox 스크롤바 숨김 */
  -ms-overflow-style: none;
  /* IE/Edge 스크롤바 숨김 */
}

.filePreviewContainer::-webkit-scrollbar {
  display: none;
  /* Chrome/Safari 스크롤바 숨김 */
}

/* 개별 파일 미리보기 아이템 */
.filePreviewItem {
  position: relative;
  flex-shrink: 0;
  /* 아이템 크기 유지 */
  width: 64px;
  /* Figma 기준 너비 */
  height: 64px;
  /* Figma 기준 높이 */
  border-radius: 6px;
  /* Figma 기준 */
  overflow: hidden;
  box-shadow: 0px 1px 4px 0px rgba(12, 12, 13, 0.14);
  /* Figma 기준 그림자 */
}

/* 파일 썸네일 이미지 */
.fileThumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 이미지 비율 유지하며 채움 */
}

/* 이미지 외 파일 타입 표시 (임시) */
.fileIconPlaceholder {
  width: 100%;
  height: 100%;
  background-color: #f3f4f6;
  /* 밝은 회색 배경 */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

/* 파일 제거 버튼 */
.removeFileButton {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  /* 반투명 검정 배경 */
  border: none;
  border-radius: 50%;
  width: 16px;
  /* Figma 기준 24px 프레임, 아이콘은 더 작음 */
  height: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0px 0px 16px -8px rgba(12, 12, 13, 0.1);
  /* Figma 기준 그림자 */
}

/* 제거 버튼 아이콘 */
.removeIcon {
  width: 8px;
  /* 아이콘 크기 조정 */
  height: 8px;
  filter: brightness(0) invert(1);
  /* 아이콘 흰색으로 */
}

.choicesInputContainer {
  display: flex;
  flex-wrap: wrap;
  /* 줄바꿈 허용 */
  justify-content: center;
  /* 가운데 정렬 */
  gap: 8px;
  /* 버튼 간 간격 */
  padding: 12px 16px;
  /* 패딩 추가 */
  overflow-y: auto;
}

.inputChoiceButton {
  background-color: #f8f4f6;
  /* Figma */
  border: none;
  /* Figma에는 테두리 없음 */
  border-radius: 12px;
  /* Figma */
  padding: 10px 14px;
  /* Figma 근사치 조정 */
  color: #111111;
  /* Figma */
  font-size: 13px;
  /* Figma와 유사하게 조정 */
  font-weight: 400;
  /* Figma */
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  /* 버튼 크기 유지 */
}

.inputChoiceButton:hover {
  background-color: #fceff9;
  /* 약간 더 진한 분홍색 (호버 효과) */
}

/* 텍스트 입력 영역 */
.textInputContainer {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 8px 16px;
}

.attachButton {
  background: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  margin-top: 1px;
}

.attachIcon {
  width: 16px;
  height: 16px;
  color: #9ca3af;
  /* Figma Gray 400 */
  transition: color 0.2s ease;
}

.attachButton:hover .attachIcon {
  color: #6b7280;
  /* Figma Gray 500 */
}

.textInputField {
  flex-grow: 1;
  border: none;
  background-color: transparent;
  resize: none;
  max-height: 100px;
  /* 최대 높이 (넘으면 스크롤 생김 - overflow-y:auto 필요) */
  min-height: 18px;
  /* 최소 높이 */
  outline: none;
  /* 포커스 시 외곽선 제거 */
  height: auto;
}

.textInputField::placeholder {
  color: #9ca3af;
  /* Figma Gray 400 / placeholder 색상 */
}

.sendButton {
  background: none;
  border: none;
  padding: 6px;
  /* 아이콘 주변 여백 */
  cursor: pointer;
  align-self: flex-end;
  /* 하단 정렬 유지 */
  margin-bottom: 2px;
  /* textarea padding 고려 미세 조정 */
}

.sendButton:disabled {
  cursor: not-allowed;
}

.sendIcon {
  width: 20px;
  height: 20px;
  color: #9ca3af;
  /* Figma Gray 400 / 비활성 색상 */
  transition: color 0.2s ease;
}

.sendButton:not(:disabled) .sendIcon {
  color: #cf1578;
  /* Figma Primary Pink / 활성 색상 */
}

/* 모바일 반응형 */
@media (max-width: 480px) {
  .chatWindow {
    width: 100%;
    height: 100%;
    border-radius: 0;
    /* position: static; */
    /* 불필요하므로 제거 또는 유지 */
    /* bottom: auto; */
    /* 불필요하므로 제거 */
    /* right: auto; */
    /* 불필요하므로 제거 */
    box-shadow: none;
    margin-bottom: 0;
    /* 전체 화면 차지하므로 margin 제거 */
  }

  .chatContent {
    /* 모바일 스크롤 최적화 추가 */
    scroll-behavior: smooth;
    will-change: scroll-position;
    /* GPU 가속 사용 */
  }

  .chatInputArea {
    max-height: none;
    /* 모바일에서는 최대 높이 제한 해제 */
  }
}

/* 전송 버튼 스피너 스타일 */
.sendButton .animate-spin {
  width: 16px;
  height: 16px;
  color: #cf1578;
  /* 활성 색상 */
}

/* SVG 스피너 스타일 */
.sendingSpinner {
  animation: spin 1s linear infinite;
  width: 16px;
  /* Match previous icon size */
  height: 16px;
  color: #cf1578;
  /* Active color */
}

/* Default Send Icon Placeholder/Style */
.sendIconPlaceholder {
  display: inline-block;
  /* Or block */
  width: 20px;
  height: 20px;
  /* Default/disabled color */
  /* Use mask or background-image to show the actual icon */
  /* Example using mask: */
  /* mask-image: url('@/assets/icons/send-icon.svg'); */
  /* mask-size: contain; */
  /* mask-repeat: no-repeat; */
  /* mask-position: center; */
  transition: background-color 0.2s ease;
}

.sendButton:not(:disabled) .sendIconPlaceholder {
  background-color: #cf1578;
  /* Active color */
}

.sendButton:disabled .sendIconPlaceholder {
  background-color: #d1d5db;
  /* Explicit disabled color */
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* 파일 업로드 버튼 컨테이너 */
.fileInputContainer {
  display: flex;
  padding: 12px 16px;
  /* preview.vue p-3과 유사하게 조정 */
  border-top: 1px solid #eeeeee;
  /* preview.vue border-t border-gray-100 */
}

/* 파일 업로드 버튼 (preview.vue 스타일 기반) */
.fileUploadButton {
  width: 100%;
  /* preview.vue w-full */
  padding: 12px 16px;
  /* preview.vue py-3 px-4 */
  border: 1px solid #e5e7eb;
  /* preview.vue border border-gray-200 */
  background-color: #ffffff;
  /* preview.vue bg-white */
  color: #374151;
  /* preview.vue text-gray-800 근사치 */
  border-radius: 0.75rem;
  /* preview.vue rounded-xl */
  cursor: pointer;
  /* preview.vue cursor-pointer */
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;
  /* preview.vue transition duration-200 */
  font-size: 14px;
  /* preview.vue text-sm */
  font-weight: 400;
  /* preview.vue font-normal */
  text-align: center;
  line-height: 1.5;
}

.fileUploadButton:hover {
  background-color: #f9fafb;
  /* preview.vue hover:enabled:bg-gray-50 */
}

.fileUploadButton:focus {
  outline: none;
  /* preview.vue focus:outline-none */
  /* focus-visible 상태에만 링을 표시하는 것이 좋지만, CSS만으로는 어려움 */
  /* box-shadow: 0 0 0 1px #D1D5DB; */
  /* preview.vue focus:ring-1 focus:ring-gray-300 - 단순화 */
  border-color: #9ca3af;
  /* preview.vue focus:border-gray-300 - 약간 더 진하게 */
}

/* 비활성화 스타일 (필요 시 추가) */
.fileUploadButton:disabled {
  border-color: #e5e7eb;
  /* preview.vue disabled:border-gray-200 */
  color: #9ca3af;
  /* preview.vue disabled:text-gray-400 */
  background-color: #f9fafb;
  /* preview.vue disabled:bg-gray-50 */
  cursor: not-allowed;
  /* preview.vue disabled:cursor-not-allowed */
}

/* 종료 상태 입력 영역 */
.closedInputArea {
  margin-top: 16px;
  padding: 8px 8px;
  /* Figma Frame 31346 padding + 내부 Frame 31334 padding 고려 */
  background-color: #ffffff;
  /* Figma fill_AJM0GE */
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Figma Frame 31346 gap */
  flex-shrink: 0;
}

.feedbackContainer,
.newChatContainer {
  display: flex;
  width: 100%;
}

.feedbackButton {
  flex-grow: 1;
  padding: 10px;
  /* Figma Frame 31344 padding */
  border-radius: 8px;
  /* Figma Frame 31344 borderRadius */
  font-size: 14px;
  /* Figma style_GFTCHV & style_9EPADM */
  font-weight: 700;
  /* Figma style_GFTCHV */
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  border: none;
  display: flex;
  /* 내부 텍스트 중앙 정렬 위해 */
  justify-content: center;
  align-items: center;
  height: 44px;
  /* Figma Frame 31344 height */
}

/* Figma Frame 31344 스타일 (Primary Button) */
.feedbackButton.primary {
  background-color: #d01578;
  /* Figma fill_XNLN3V */
  color: #ffffff;
  /* Figma fill_AJM0GE */
  box-shadow:
    0px 1px 4px 0px rgba(12, 12, 13, 0.05),
    0px 1px 4px 0px rgba(12, 12, 13, 0.1);
  /* Figma effect_7HLSJG */
}

.feedbackButton.primary:hover {
  background-color: #a81061;
  /* 약간 어둡게 */
}

/* Figma Frame 31319 + 31317 스타일 (Secondary Button) */
.feedbackButton.secondary {
  background-color: #ffffff;
  /* 버튼 배경 */
  color: #111111;
  /* Figma fill_T6VNVS */
  border: 0.5px solid #d3d3d3;
  /* Figma stroke_K11QEJ */
  font-weight: 400;
  /* Figma style_9EPADM */
}

.feedbackButton.secondary:hover {
  background-color: #f8f8f8;
  /* 약간 회색 */
  border-color: #b0b0b0;
}

.feedbackButton.primary:disabled {
  background-color: #f8f8f8;
  box-shadow: none;
  color: #b0b0b0;
  cursor: not-allowed;
}

/* 상담사 대기중 배너 스타일 */
.waitingForAgent {
  background-color: var(--wowpress-secondary-color, #ffecf6);
  /* 연한 핑크 배경 */
  color: var(--wowpress-primary-color, #cf1578);
  /* 주요 색상 텍스트 */
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
  align-self: stretch;
  margin: 16px auto;
  /* 위아래 여백, 가로 중앙 정렬 */
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid var(--wowpress-primary-color, #cf1578);
}

.waitingForAgent p {
  font-weight: 500;
  /* 중간 두께 */
  margin: 0 0 4px 0;
  /* 아래쪽 여백 살짝 */
}

.waitingForAgent span {
  font-size: 12px;
  opacity: 0.8;
}

.autoCloseBanner {
  background-color: #fffbeb;
  /* 노란색 계열 배경 */
  color: #b45309;
  /* 어두운 주황색 텍스트 */
  padding: 8px 16px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  border-bottom: 1px solid #fef3c7;
  /* 연한 노란색 테두리 */
  flex-shrink: 0;
  /* 높이 고정 */
}

.autoCloseBanner p {
  margin: 0;
}

/* 알림 스타일 추가 (ChatExpanded와 유사하게) */
.localNotification {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 90%;
  min-width: 280px;
  /* backdrop-filter: blur(8px); */
  /* ChatWindow 배경이 흰색이므로 blur 불필요 */
}
</style>
